
import { useState, useEffect } from 'react';
import { fetchPublicFundOverview, type FundOverviewData } from '../../../api/dripPlan';

export default function FundOverview() {
  const [fundOverview, setFundOverview] = useState<FundOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [animatedIncome, setAnimatedIncome] = useState(0);
  const [animatedExpense, setAnimatedExpense] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchPublicFundOverview()
      .then((data) => {
        if (!cancelled) setFundOverview(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!fundOverview) return;
    const duration = 2000;
    const steps = 60;
    const incomeStep = fundOverview.totalIncome / steps;
    const expenseStep = fundOverview.totalExpense / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setAnimatedIncome(Math.min(incomeStep * currentStep, fundOverview.totalIncome));
      setAnimatedExpense(Math.min(expenseStep * currentStep, fundOverview.totalExpense));

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [fundOverview]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 text-center text-[#6C6C6C]">
        加载资金概览...
      </div>
    );
  }

  if (!fundOverview) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 text-center text-[#6C6C6C]">
        暂无资金数据
      </div>
    );
  }

  const balance = fundOverview.balance;
  const maxTrendValue = Math.max(...fundOverview.monthlyTrend.map((item) => item.value), 1);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#1E1E1E] mb-4 md:mb-6">资金使用总览</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-4 md:gap-6">
            <div className="flex flex-col items-center p-4 md:p-6 bg-gradient-to-br from-[#2A7A62]/5 to-[#2A7A62]/10 rounded-lg">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#2A7A62] rounded-full flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-funds-line text-2xl md:text-3xl text-white"></i>
              </div>
              <div className="text-center">
                <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">总收入</p>
                <p className="text-xl md:text-3xl font-bold text-[#2A7A62]">
                  ¥{animatedIncome.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center p-4 md:p-6 bg-gradient-to-br from-[#E06B3D]/5 to-[#E06B3D]/10 rounded-lg">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#E06B3D] rounded-full flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-shopping-cart-line text-2xl md:text-3xl text-white"></i>
              </div>
              <div className="text-center">
                <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">总支出</p>
                <p className="text-xl md:text-3xl font-bold text-[#E06B3D]">
                  ¥{animatedExpense.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-br from-[#F4B942]/5 to-[#F4B942]/10 rounded-lg">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-[#F4B942] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-wallet-3-line text-2xl md:text-3xl text-white"></i>
            </div>
            <div>
              <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">结余资金</p>
              <p className="text-xl md:text-3xl font-bold text-[#F4B942]">
                ¥{balance.toLocaleString('zh-CN')}
              </p>
            </div>
          </div>

          <div className="p-4 md:p-6 bg-[#F9F9F9] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs md:text-sm text-[#6C6C6C]">管理费占比</p>
              <p className="text-lg md:text-xl font-bold text-[#2A7A62]">{fundOverview.managementFeeRate}%</p>
            </div>
            <div className="w-full bg-white rounded-full h-2 md:h-3 overflow-hidden">
              <div 
                className="h-full bg-[#2A7A62] rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(fundOverview.managementFeeRate, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-[#6C6C6C] mt-2">远低于国家规定的10%标准</p>
          </div>
        </div>

        <div>
          <h3 className="text-base md:text-lg font-bold text-[#1E1E1E] mb-3 md:mb-4">支出分类明细</h3>
          {fundOverview.expenseCategories.length === 0 ? (
            <p className="text-sm text-[#6C6C6C]">暂无支出记录</p>
          ) : (
          <div className="space-y-3 md:space-y-4">
            {fundOverview.expenseCategories.map((category, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs md:text-sm font-medium text-[#1E1E1E]">{category.name}</span>
                  <span className="text-xs md:text-sm font-bold text-[#2A7A62]">
                    ¥{category.value.toLocaleString('zh-CN')} ({category.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-[#F9F9F9] rounded-full h-6 md:h-8 overflow-hidden relative">
                  <div 
                    className="h-full bg-gradient-to-r from-[#2A7A62] to-[#2A7A62]/80 rounded-full transition-all duration-1000 flex items-center justify-end pr-2 md:pr-3"
                    style={{ width: `${category.percentage}%` }}
                  >
                    <span className="text-xs font-medium text-white">{category.percentage}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}

          <div className="mt-6 md:mt-8 p-4 md:p-6 bg-gradient-to-br from-[#2A7A62]/5 to-[#2A7A62]/10 rounded-lg border-l-4 border-[#2A7A62]">
            <div className="flex items-start gap-2 md:gap-3">
              <i className="ri-information-line text-lg md:text-xl text-[#2A7A62] mt-1"></i>
              <div>
                <p className="text-xs md:text-sm font-medium text-[#1E1E1E] mb-1">资金使用说明</p>
                <p className="text-xs text-[#6C6C6C] leading-relaxed">
                  所有资金使用均经过严格审批流程，定期接受第三方审计机构审计。我们承诺将每一分善款用在实处，确保透明、高效地服务一线工作者。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 md:mt-8">
        <h3 className="text-base md:text-lg font-bold text-[#1E1E1E] mb-4 md:mb-6">月度发放趋势</h3>
        <div className="bg-[#F9F9F9] rounded-lg p-4 md:p-6">
          <div className="flex items-end justify-between gap-1 md:gap-2 h-40 md:h-48">
            {fundOverview.monthlyTrend.map((item, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-1 md:gap-2">
                <div 
                  className="w-full bg-gradient-to-t from-[#2A7A62] to-[#3A9A82] rounded-t-sm md:rounded-t transition-all duration-500 hover:from-[#236854] hover:to-[#2A7A62] cursor-pointer relative group"
                  style={{ height: `${(item.value / maxTrendValue) * 100}%`, minHeight: '8px' }}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1E1E1E] text-white text-[9px] md:text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    ¥{(item.value / 10000).toFixed(1)}万
                  </div>
                </div>
                <span className="text-[8px] md:text-xs text-[#6C6C6C]">{item.month.replace('月', '')}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#6C6C6C]">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-gradient-to-t from-[#2A7A62] to-[#3A9A82] rounded-sm"></span>
              月度发放金额
            </span>
            <span>单位：万元</span>
          </div>
        </div>
      </div>
    </div>
  );
}
